import type React from "react";
import { useCallback } from "react";
import { Avatar, AvatarFallback } from "@planta-v2/pds-ui/avatar";
import { PlantaAvatarImage } from "@planta-v2/pds-ui/planta-avatar-image";
import { StringUtils } from "@frontend/shared/presentation/utils/string.utils";
import { DateUtils } from "@frontend/shared/presentation/utils/date.utils";
import { useScheduledImageSrc } from "@frontend/shared/presentation/hooks/useScheduledImageSrc";
import type { ConnectionRequestSummary } from "@shared/schemas/connection-request.schemas";
import {
  ConnectionRequestIdPublic,
  type EventParticipantIdPublic,
} from "@shared/schemas/branded-id.schemas";
import { formatProfileSubtitle } from "@shared/utils/profile-subtitle.utils";
import { IntroRequestStatus } from "@shared/schemas/intro-request-state.schemas";
import { i18n } from "@frontend/i18n";
import { ProfileSubtitleWithLink } from "./ProfileSubtitleWithLink";

interface SentRequestCardProps {
  readonly request: ConnectionRequestSummary;
  readonly onViewProfile: (
    participantIdPublic: EventParticipantIdPublic,
  ) => void;
}

export function SentRequestCard({
  request,
  onViewProfile,
}: SentRequestCardProps): React.JSX.Element {
  const name = request.toUserName;
  const photoUrl = request.toUserPhotoUrl;
  const headline = request.toUserHeadline;
  const company = request.toUserCompany;
  const message = request.typeData.message;
  const toParticipantIdPublic = request.toParticipantIdPublic;

  const isConnected = request.typeData.status === IntroRequestStatus.ACCEPTED;
  const { ref, scheduledSrc, onLoadingStatusChange } =
    useScheduledImageSrc(photoUrl);

  const subtitle = formatProfileSubtitle(
    { title: headline, companyName: company },
    ({ position, company: companyName }) =>
      i18n.t("shared.profile.subtitle.withCompany", {
        position,
        company: companyName,
      }),
  );

  const sentTimeAgo = DateUtils.formatRelativeDate(
    request.createdAt,
    i18n.getLocale(),
  );

  const handleCardClick = useCallback((): void => {
    onViewProfile(toParticipantIdPublic);
  }, [onViewProfile, toParticipantIdPublic]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleCardClick();
      }
    },
    [handleCardClick],
  );

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      data-testid={`sent-request-card-${ConnectionRequestIdPublic.unwrap(
        request.idPublic,
      )}`}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      className="w-full border-b border-foreground/10 py-4 text-left transition-colors last:border-b-0 hover:bg-accent/50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring"
    >
      {/* Header row: Avatar, Name/Subtitle, Full Profile link */}
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10 shrink-0 rounded-full border border-foreground/10">
          {scheduledSrc !== null ? (
            <PlantaAvatarImage
              src={scheduledSrc}
              alt={name}
              onLoadingStatusChange={onLoadingStatusChange}
            />
          ) : null}
          <AvatarFallback className="bg-muted-foreground/20 font-serif text-sm font-normal text-foreground/75">
            {StringUtils.getInitials(name)}
          </AvatarFallback>
        </Avatar>

        <div className="flex flex-col min-w-0 flex-1 gap-0.5">
          <div className="flex items-center gap-2">
            <span
              data-testid="sent-request-recipient-name"
              className="font-serif text-base font-normal text-foreground leading-tight truncate"
            >
              {name}
            </span>
            {isConnected && (
              <span
                data-testid="sent-request-connected-badge"
                className="shrink-0 rounded-full bg-success/15 px-2 py-0.5 text-pds-2xs font-medium text-success"
              >
                {i18n.t("user.directory.requests.sent.connected")}
              </span>
            )}
          </div>
          {/* Subtitle + Full profile on same line */}
          <ProfileSubtitleWithLink subtitle={subtitle} />
        </div>
      </div>

      {/* Message content (if provided) */}
      {message !== null && message.content !== "" && (
        <p
          data-testid="sent-request-message"
          className="mt-3 text-sm text-foreground leading-relaxed"
        >
          {message.content}
        </p>
      )}

      {/* Sent timestamp */}
      <p
        data-testid="sent-request-timestamp"
        className="mt-3 text-pds-2xs text-muted-foreground"
      >
        {i18n.t("user.directory.requests.sent.sentAgo", { time: sentTimeAgo })}
      </p>
    </div>
  );
}
